// Propojení s externími kalendáři (úkol 4.2): stav Google a Outlooku, výběr kalendáře a směru,
// ruční synchronizace a odpojení. Chyby se vracejí jako klíč i18n, překládá je až view.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type {
  CalendarChoice,
  CalendarLinkStatus,
  CalendarProvider,
  CalendarSyncResult,
  UpdateCalendarLinkInput,
} from '@wac-buddy/shared'
import {
  disconnectCalendar,
  getCalendars,
  getConnectUrl,
  listProviderCalendars,
  syncCalendar,
  updateCalendarLink,
} from '@/api/calendars'
import { ApiClientError } from '@/api/client'
import { useCalendarStore } from './calendar'

export type CalendarsStatus = 'idle' | 'loading' | 'ready' | 'error'
export type CalendarActionResult = { ok: true } | { ok: false; errorKey: string }
export type CalendarSyncOutcome =
  { ok: true; result: CalendarSyncResult } | { ok: false; errorKey: string }

const ok: CalendarActionResult = { ok: true }
const fail = (errorKey: string): { ok: false; errorKey: string } => ({ ok: false, errorKey })
const failure = (e: unknown, key: string): { ok: false; errorKey: string } =>
  fail(e instanceof ApiClientError ? key : 'common.network')

export const useCalendarsStore = defineStore('calendars', () => {
  const links = ref<CalendarLinkStatus[]>([])
  const status = ref<CalendarsStatus>('idle')
  const busy = ref(false)
  /** Poskytovatel, se kterým právě běží akce (spinner jen na jeho kartě). */
  const busyProvider = ref<CalendarProvider | null>(null)
  const choices = ref<Partial<Record<CalendarProvider, CalendarChoice[]>>>({})

  let epoch = 0

  /** Načte stav obou poskytovatelů. `silent`: data už jsou vidět, chyba ani načítání obrazovku nerozbijí. */
  async function load(silent = false): Promise<void> {
    const seq = epoch
    if (!silent || links.value.length === 0) status.value = 'loading'
    try {
      const res = await getCalendars()
      if (seq !== epoch) return
      links.value = res.data
      status.value = 'ready'
    } catch {
      if (seq !== epoch) return
      if (!silent || links.value.length === 0) status.value = 'error'
    }
  }

  function begin(provider: CalendarProvider): boolean {
    if (busy.value) return false
    busy.value = true
    busyProvider.value = provider
    return true
  }

  function end(): void {
    busy.value = false
    busyProvider.value = null
  }

  /** Získá adresu souhlasu a přesměruje celé okno. Po návratu přijde uživatel na /nastaveni/kalendare. */
  async function connect(provider: CalendarProvider): Promise<CalendarActionResult> {
    if (!begin(provider)) return fail('common.error')
    try {
      const { url } = await getConnectUrl(provider)
      window.location.assign(url)
      return ok
    } catch (e) {
      return failure(e, 'settings.calendars.errors.connect')
    } finally {
      end()
    }
  }

  /** Kalendáře u poskytovatele pro výběr. Vrací seznam, nebo klíč chyby. */
  async function loadChoices(
    provider: CalendarProvider,
  ): Promise<{ ok: true; data: CalendarChoice[] } | { ok: false; errorKey: string }> {
    const seq = epoch
    try {
      const res = await listProviderCalendars(provider)
      if (seq === epoch) choices.value = { ...choices.value, [provider]: res.data }
      return { ok: true, data: res.data }
    } catch (e) {
      return failure(e, 'settings.calendars.errors.choices')
    }
  }

  function replaceLink(next: CalendarLinkStatus): void {
    links.value = links.value.map((l) => (l.provider === next.provider ? next : l))
  }

  async function save(
    provider: CalendarProvider,
    input: UpdateCalendarLinkInput,
  ): Promise<CalendarActionResult> {
    if (!begin(provider)) return fail('common.error')
    const seq = epoch
    try {
      const next = await updateCalendarLink(provider, input)
      if (seq === epoch) replaceLink(next)
      return ok
    } catch (e) {
      return failure(e, 'settings.calendars.errors.save')
    } finally {
      end()
    }
  }

  /** Ruční synchronizace. 429 = poslední běh byl před méně než 30 s. */
  async function sync(provider: CalendarProvider): Promise<CalendarSyncOutcome> {
    if (!begin(provider)) return fail('common.error')
    try {
      const result = await syncCalendar(provider)
      // Synchronizace mění události v kalendáři i počty v Nastavení.
      useCalendarStore().invalidateCache()
      await load(true)
      return { ok: true, result }
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 429) {
        return fail('settings.calendars.errors.tooSoon')
      }
      return failure(e, 'settings.calendars.errors.sync')
    } finally {
      end()
    }
  }

  async function disconnect(provider: CalendarProvider): Promise<CalendarActionResult> {
    if (!begin(provider)) return fail('common.error')
    try {
      await disconnectCalendar(provider)
      choices.value = { ...choices.value, [provider]: undefined }
      useCalendarStore().invalidateCache()
      await load(true)
      return ok
    } catch (e) {
      return failure(e, 'settings.calendars.errors.disconnect')
    } finally {
      end()
    }
  }

  function invalidate(): void {
    epoch++
    links.value = []
    choices.value = {}
    status.value = 'idle'
    end()
  }

  return {
    links,
    status,
    busy,
    busyProvider,
    choices,
    load,
    connect,
    loadChoices,
    save,
    sync,
    disconnect,
    invalidate,
  }
})
